import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

export interface SettingRowProps {
  /** What the setting is. */
  label: string;
  /** The id of the control, when it has one, so a click on the label reaches it. */
  htmlFor?: string;
  /** What it means as it stands now. Replaced by `error` when there is one. */
  description?: React.ReactNode;
  error?: string | null;
  /** The control: a switch, a select, a button. */
  children: React.ReactNode;
}

/**
 * One setting, as a row: what it is and what it means on the left, its
 * control on the right. Every setting in a list is laid out this way, so a
 * switch, a choice and a button line up down the same edge. On a narrow
 * screen the control drops under the words rather than squeezing them.
 */
export function SettingRow({ label, htmlFor, description, error = null, children }: SettingRowProps) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between"
      sx={{ flexWrap: 'wrap', columnGap: 2, rowGap: 1 }}>
      <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
        <Typography
          component={htmlFor ? 'label' : 'div'}
          htmlFor={htmlFor}
          variant="subtitle2"
          sx={(t) => ({ color: t.tokens.text.primary, cursor: htmlFor ? 'pointer' : 'default' })}
        >
          {label}
        </Typography>
        {error || description ? (
          <Typography
            variant="body2"
            role={error ? 'alert' : undefined}
            sx={(t) => ({ mt: 0.25, color: error ? t.tokens.semantic.danger : t.tokens.text.secondary })}
          >
            {error || description}
          </Typography>
        ) : null}
      </Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
        {children}
      </Stack>
    </Stack>
  );
}
